import { useState } from 'react';
import { Loader2, Upload, CheckCircle2, Briefcase, Users, Clock, Heart, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Reveal } from '@/components/ui/Reveal';
import { useSEO } from '@/lib/seo';
import api from '@/lib/api';
import { parseApiError } from '@/lib/parseApiError';
import { toast } from 'sonner';

export default function Recrutement() {
  useSEO({
    title: "Recrutement | Groupe Ambulances Beaulon - DEA, auxiliaire, taxi",
    description: "Le Groupe Ambulances Beaulon recrute : DEA, auxiliaire ambulancier, taxi conventionné. Rejoignez une équipe humaine et engagée dans l'Allier.",
    keywords: "recrutement ambulance allier, emploi DEA, auxiliaire ambulancier, taxi conventionné emploi"
  });

  const [form, setForm] = useState({ nom: '', prenom: '', email: '', telephone: '', poste: '', message: '', rgpd: false });
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rgpd) { toast.error('Vous devez accepter la politique RGPD.'); return; }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (cv) data.append('cv', cv);
      const res = await api.post('/recrutement', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(parseApiError(err, "Erreur lors de l'envoi. Merci de réessayer."), { duration: 6000 });
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="section-pad">
        <div className="container-app max-w-2xl">
          <div className="card-base p-8 text-center" data-testid="recrutement-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="h2 mt-4 text-anthracite-950">Candidature envoyée</h1>
            <p className="lead mt-2">{success.message}</p>
            <p className="mt-4 text-sm text-anthracite-700">Référence : <span className="font-mono font-semibold">{success.reference}</span></p>
            <Button className="mt-6" onClick={() => { setSuccess(null); setForm({ nom: '', prenom: '', email: '', telephone: '', poste: '', message: '', rgpd: false }); setCv(null); }}>Envoyer une autre candidature</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="hero-wash">
        <div className="container-app pt-14 pb-10 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div>
              <p className="badge-soft mb-3">Recrutement</p>
              <h1 className="h1 text-anthracite-950">Rejoignez une équipe humaine et engagée</h1>
              <p className="lead mt-4 max-w-xl">Notre groupe se développe et recrute pour accompagner sa croissance. Si vous partagez nos valeurs, parlons-en.</p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Users, label: 'Esprit d’équipe' },
                { icon: Clock, label: 'Planning organisé' },
                { icon: Briefcase, label: 'Véhicules récents' },
                { icon: Heart, label: 'Ambiance humaine' },
              ].map(it => (
                <div key={it.label} className="card-base p-4 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center"><it.icon className="h-5 w-5" /></div>
                  <span className="text-sm font-medium text-anthracite-900">{it.label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-app grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-4">
            <h2 className="h2 text-anthracite-950">Postes à pourvoir</h2>
            {[
              { code: 'DEA', label: 'Diplômé d’État Ambulancier', desc: 'Transport sanitaire de patients en état nécessitant des soins.' },
              { code: 'AUX', label: 'Auxiliaire ambulancier', desc: 'Assistance au DEA, prise en charge en équipe.' },
              { code: 'TAXI', label: 'Taxi conventionné', desc: 'Transport de patients en taxi conventionné par l’Assurance Maladie.' },
            ].map(p => (
              <article key={p.code} className="card-base p-5">
                <div className="flex items-center gap-2 text-brand-700 text-xs uppercase tracking-wide font-semibold">{p.code}</div>
                <h3 className="font-heading font-semibold text-anthracite-950 mt-1">{p.label}</h3>
                <p className="text-sm text-anthracite-700 mt-2">{p.desc}</p>
              </article>
            ))}
          </div>

          <form onSubmit={submit} className="lg:col-span-2 card-base p-6 space-y-5" data-testid="recruitment-form">
            <h2 className="h3 text-anthracite-950">Candidature</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Nom *</Label><Input required value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} className="mt-1.5" data-testid="recrutement-nom" /></div>
              <div><Label>Prénom *</Label><Input required value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))} className="mt-1.5" data-testid="recrutement-prenom" /></div>
              <div><Label>Email *</Label><Input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1.5" data-testid="recrutement-email" /></div>
              <div><Label>Téléphone *</Label><Input type="tel" required value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} className="mt-1.5" data-testid="recrutement-telephone" /></div>
            </div>
            <div>
              <Label>Poste recherché *</Label>
              <Select value={form.poste} onValueChange={v => setForm(f => ({ ...f, poste: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="recrutement-poste"><SelectValue placeholder="Sélectionner un poste" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="DEA">Diplômé d'État Ambulancier (DEA)</SelectItem>
                  <SelectItem value="Auxiliaire">Auxiliaire ambulancier</SelectItem>
                  <SelectItem value="Taxi">Taxi conventionné</SelectItem>
                  <SelectItem value="Autre">Autre / candidature spontanée</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Présentation, expérience, disponibilité..." className="mt-1.5 min-h-[120px]" />
            </div>
            <div>
              <Label>CV (PDF, DOC, DOCX - max 5 Mo)</Label>
              <label className="mt-2 flex items-center gap-3 rounded-lg border border-dashed border-anthracite-200 px-4 py-4 cursor-pointer hover:bg-anthracite-100/50">
                <Upload className="h-5 w-5 text-brand-700" />
                <span className="text-sm text-anthracite-800 flex-1">{cv ? cv.name : 'Cliquez pour sélectionner votre CV'}</span>
                <input type="file" accept=".pdf,.doc,.docx" onChange={e => setCv(e.target.files?.[0] || null)} className="hidden" data-testid="recruitment-cv-upload-input" />
              </label>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.rgpd} onCheckedChange={(c) => setForm(f => ({ ...f, rgpd: !!c }))} data-testid="recrutement-rgpd" />
              <span className="text-sm text-anthracite-800">J'accepte que ma candidature et mon CV soient utilisés par le Groupe Ambulances Beaulon à des fins de recrutement.</span>
            </label>
            <Button type="submit" disabled={loading} className="w-full" size="lg" data-testid="recrutement-submit-button">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi...</> : <>Envoyer ma candidature <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
